import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalibrationDrilldown } from '@/api/queries/kpi-metrics-queries'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type {
	CalibrationOrigin,
	DirecionadaCalibrationBreakdown,
	InteracaoCalibrationBreakdown,
	KpiMetricsOutput,
	KpiMetricsParams,
	VereAgirCalibrationBreakdown,
} from '@/types'
import { BarListRow } from './shared/bar-list-row'

const ANSWER_COLOR: Record<string, string> = {
	COMPLIANT: 'bg-kaefer-grass-green',
	NON_COMPLIANT: 'bg-kaefer-red',
	NOT_APPLICABLE: 'bg-kaefer-medium-grey',
}

const ANSWER_LABEL_KEY: Record<string, string> = {
	COMPLIANT: 'kpiMetrics.direcionada.compliant',
	NON_COMPLIANT: 'kpiMetrics.direcionada.nonCompliant',
	NOT_APPLICABLE: 'kpiMetrics.direcionada.notApplicable',
}

interface RankingPerson {
	personId: string
	personName: string
	count: number
	congratulated?: number
	oriented?: number
}

interface RankingColumnProps {
	title: string
	origin: CalibrationOrigin
	ranking: RankingPerson[]
	params?: KpiMetricsParams
}

export function CalibracaoDashboard({
	data,
	params,
}: {
	data: KpiMetricsOutput
	params?: KpiMetricsParams
}) {
	const { t } = useTranslation()

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
			<RankingColumn
				title={t('kpiMetrics.calibracao.vereAgir')}
				origin="VEREAGIR"
				ranking={data.calibrationRanking.vereAgir}
				params={params}
			/>
			<RankingColumn
				title={t('kpiMetrics.calibracao.interacao')}
				origin="INTERACAO"
				ranking={data.calibrationRanking.interacao}
				params={params}
			/>
			<RankingColumn
				title={t('kpiMetrics.calibracao.direcionada')}
				origin="DIRECIONADA"
				ranking={data.calibrationRanking.direcionada}
				params={params}
			/>
		</div>
	)
}

function RankingColumn({ title, origin, ranking, params }: RankingColumnProps) {
	const { t } = useTranslation()
	const [selected, setSelected] = useState<{ id: string; name: string } | null>(
		null,
	)
	const { data: drilldown, isLoading } = useCalibrationDrilldown(
		selected?.id,
		origin,
		params,
	)

	const max = Math.max(1, ...ranking.map((r) => r.count))

	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			{selected ? (
				<>
					<button
						type="button"
						onClick={() => setSelected(null)}
						className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-kaefer-medium-grey hover:text-kaefer-dark-grey"
					>
						<ArrowLeft className="size-3.5" />
						{t('kpiMetrics.calibracao.back')}
					</button>
					<p className="mb-3 truncate text-sm font-semibold text-kaefer-dark-grey">
						{selected.name}
					</p>
					{isLoading ? (
						<div className="flex flex-col gap-2">
							{['a', 'b', 'c'].map((key) => (
								<Skeleton key={key} className="h-8 w-full" />
							))}
						</div>
					) : (
						<CalibrationDrilldownView origin={origin} drilldown={drilldown} />
					)}
				</>
			) : (
				<>
					<span className="text-sm font-semibold text-kaefer-dark-grey">
						{title}
					</span>
					{ranking.length === 0 ? (
						<p className="py-10 text-center text-sm text-muted-foreground">
							{t('kpiMetrics.charts.noData')}
						</p>
					) : (
						<div className="mt-3 flex max-h-96 flex-col gap-2 overflow-y-auto pr-1">
							{ranking.map((person) => (
								<button
									key={person.personId}
									type="button"
									onClick={() =>
										setSelected({
											id: person.personId,
											name: person.personName,
										})
									}
									className={cn(
										'w-full cursor-pointer rounded-md p-1 text-left transition-colors hover:bg-muted/40',
									)}
								>
									{origin === 'INTERACAO' ? (
										<InteracaoRankingRow person={person} max={max} />
									) : (
										<BarListRow
											label={person.personName}
											displayValue={String(person.count)}
											widthPct={(person.count / max) * 100}
											colorClass="bg-kaefer-red"
										/>
									)}
								</button>
							))}
						</div>
					)}
				</>
			)}
		</div>
	)
}

function InteracaoRankingRow({
	person,
	max,
}: {
	person: RankingPerson
	max: number
}) {
	const { t } = useTranslation()
	const oriented = person.oriented ?? 0
	const congratulated = person.congratulated ?? 0

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="grid grid-cols-[1fr_2fr_auto] items-center gap-2.5">
					<span
						className="truncate text-xs text-kaefer-dark-grey"
						title={person.personName}
					>
						{person.personName}
					</span>
					<div className="flex h-3.5 overflow-hidden rounded-full bg-kaefer-light-silver/60">
						{oriented > 0 && (
							<div
								className="h-full bg-kaefer-red"
								style={{ width: `${(oriented / max) * 100}%` }}
							/>
						)}
						{congratulated > 0 && (
							<div
								className="h-full bg-kaefer-grass-green"
								style={{ width: `${(congratulated / max) * 100}%` }}
							/>
						)}
					</div>
					<span className="text-right text-xs font-semibold text-muted-foreground tabular-nums">
						{person.count}
					</span>
				</div>
			</TooltipTrigger>
			<TooltipContent>
				<div className="flex flex-col gap-0.5 text-xs">
					<span>
						{t('kpiMetrics.interacao.oriented')}: {oriented}
					</span>
					<span>
						{t('kpiMetrics.interacao.congratulated')}: {congratulated}
					</span>
				</div>
			</TooltipContent>
		</Tooltip>
	)
}

function StatPill({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-md border border-kaefer-light-silver/60 px-2.5 py-1.5 text-center">
			<p className="text-base font-semibold text-kaefer-dark-grey">{value}</p>
			<p className="text-[11px] text-muted-foreground">{label}</p>
		</div>
	)
}

function VereAgirDrilldownView({
	data,
}: {
	data: VereAgirCalibrationBreakdown
}) {
	const { t } = useTranslation()

	if (data.total === 0) {
		return (
			<p className="py-10 text-center text-sm text-muted-foreground">
				{t('kpiMetrics.charts.noData')}
			</p>
		)
	}

	return (
		<div className="flex max-h-96 flex-col gap-3 overflow-y-auto pr-1">
			<div className="grid grid-cols-3 gap-1.5">
				<StatPill label={t('kpiMetrics.calibracao.total')} value={data.total} />
				<StatPill
					label={t('kpiMetrics.calibracao.resolved')}
					value={data.resolved}
				/>
				<StatPill
					label={t('kpiMetrics.calibracao.inProgress')}
					value={data.inProgress}
				/>
				<StatPill
					label={t('kpiMetrics.calibracao.stopAndFix')}
					value={data.stopAndFix}
				/>
				<StatPill
					label={t('kpiMetrics.calibracao.fixAndContinue')}
					value={data.fixAndContinue}
				/>
			</div>
			<div>
				<p className="mb-1.5 text-xs font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.calibracao.byKind')}
				</p>
				<div className="flex flex-col gap-2">
					{data.byKind.map((kind) => (
						<div
							key={kind.kindId}
							className="rounded-md border border-kaefer-light-silver/60 p-2.5 text-xs"
						>
							<p className="font-medium text-kaefer-dark-grey">
								{kind.kindName} · {kind.total}
							</p>
							<div className="mt-1.5 flex flex-col gap-1">
								{kind.byRisk.map((risk) => (
									<div
										key={risk.riskId}
										className="flex items-center justify-between text-muted-foreground"
									>
										<span>{risk.riskName}</span>
										<span className="font-medium text-kaefer-dark-grey">
											{risk.count}
										</span>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function InteracaoDrilldownView({
	data,
}: {
	data: InteracaoCalibrationBreakdown
}) {
	const { t } = useTranslation()

	if (data.total === 0) {
		return (
			<p className="py-10 text-center text-sm text-muted-foreground">
				{t('kpiMetrics.charts.noData')}
			</p>
		)
	}

	return (
		<div className="flex max-h-96 flex-col gap-3 overflow-y-auto pr-1">
			<div className="grid grid-cols-3 gap-1.5">
				<StatPill label={t('kpiMetrics.calibracao.total')} value={data.total} />
				<StatPill
					label={t('kpiMetrics.calibracao.congratulated')}
					value={data.congratulated}
				/>
				<StatPill
					label={t('kpiMetrics.calibracao.oriented')}
					value={data.oriented}
				/>
			</div>
			<div>
				<p className="mb-1.5 text-xs font-semibold text-kaefer-dark-grey">
					{t('kpiMetrics.calibracao.byClassification')}
				</p>
				<div className="flex flex-col gap-2">
					{data.byClassification.map((classification) => (
						<div
							key={classification.classificationId}
							className="rounded-md border border-kaefer-light-silver/60 p-2.5 text-xs"
						>
							<p className="font-medium text-kaefer-dark-grey">
								{classification.classificationName} · {classification.total}
							</p>
							<div className="mt-1.5 flex flex-col gap-1">
								{classification.bySubclassification.map((sub) => (
									<div
										key={sub.subclassificationId}
										className="flex items-center justify-between text-muted-foreground"
									>
										<span>{sub.subclassificationName}</span>
										<span className="font-medium text-kaefer-dark-grey">
											{sub.count}
										</span>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function CalibrationDrilldownView({
	origin,
	drilldown,
}: {
	origin: CalibrationOrigin
	drilldown: unknown
}) {
	if (origin === 'VEREAGIR') {
		return (
			<VereAgirDrilldownView data={drilldown as VereAgirCalibrationBreakdown} />
		)
	}

	if (origin === 'DIRECIONADA') {
		return (
			<DirecionadaDrilldownView
				data={drilldown as DirecionadaCalibrationBreakdown}
			/>
		)
	}

	return (
		<InteracaoDrilldownView data={drilldown as InteracaoCalibrationBreakdown} />
	)
}

function DirecionadaDrilldownView({
	data,
}: {
	data: DirecionadaCalibrationBreakdown
}) {
	const { t } = useTranslation()

	if (data.total === 0) {
		return (
			<p className="py-10 text-center text-sm text-muted-foreground">
				{t('kpiMetrics.charts.noData')}
			</p>
		)
	}

	const sortedQuizzes = [...data.byQuiz].sort((a, b) => b.count - a.count)
	const maxQuiz = Math.max(1, ...sortedQuizzes.map((q) => q.count))

	return (
		<div className="flex max-h-96 flex-col gap-3 overflow-y-auto pr-1">
			<StatPill label={t('kpiMetrics.calibracao.total')} value={data.total} />
			<div className="flex flex-col gap-2.5">
				{sortedQuizzes.map((quiz) => {
					const answerEntries = Object.entries(quiz.byAnswer)
					return (
						<Tooltip key={quiz.quizId}>
							<TooltipTrigger asChild>
								<div className="flex flex-col gap-1">
									<div className="flex items-center justify-between text-xs">
										<span className="truncate text-kaefer-dark-grey">
											{quiz.quizTitle}
										</span>
										<span className="tabular-nums text-muted-foreground">
											{quiz.count}
										</span>
									</div>
									<div className="flex h-3 overflow-hidden rounded-full bg-kaefer-light-silver/60">
										{answerEntries.map(([answer, value]) =>
											value > 0 ? (
												<div
													key={answer}
													className={cn(
														'h-full',
														ANSWER_COLOR[answer] ?? 'bg-kaefer-medium-grey',
													)}
													style={{ width: `${(value / maxQuiz) * 100}%` }}
												/>
											) : null,
										)}
									</div>
								</div>
							</TooltipTrigger>
							<TooltipContent side="top" align="center">
								<div className="flex flex-col gap-0.5 text-left text-xs">
									{answerEntries.map(([answer, value]) => (
										<span key={answer}>
											{ANSWER_LABEL_KEY[answer]
												? t(ANSWER_LABEL_KEY[answer])
												: answer}
											: {value} ({quiz.byAnswerPct[answer] ?? 0}%)
										</span>
									))}
								</div>
							</TooltipContent>
						</Tooltip>
					)
				})}
			</div>
		</div>
	)
}
